import { useReduxState } from 'hooks';
import { Button } from 'components/common';
import { Form, Range } from 'components/common/form';
import { EditorBlockCustomControls } from 'components/editor/blocks';

interface Props {
  altTextVisible: boolean;
  blockId: number;
  crop: boolean;
  cropLoading: boolean;
  maxWidth: number;
  onAltTextToggle: () => void;
  onCropCancel: () => void;
  onCropClick: () => Promise<void>;
  onCropSave: () => Promise<void>;
  onSetSizeCancel: () => void;
  onSetSizeChange: (size: number) => void;
  onSetSizeClick: () => void;
  onSetSizeSave: () => void;
  onReplaceClick: () => void;
  onReplaceCancel: () => void;
  replace: boolean;
  setSize: boolean;
  showReplace?: boolean;
}

const EditorBlockImageControls = ({
  altTextVisible,
  blockId,
  crop,
  cropLoading,
  maxWidth,
  onAltTextToggle,
  onCropCancel,
  onCropClick,
  onCropSave,
  onReplaceCancel,
  onReplaceClick,
  onSetSizeCancel,
  onSetSizeChange,
  onSetSizeClick,
  onSetSizeSave,
  replace,
  setSize,
  showReplace
}: Props) => {
  const { messages } = useReduxState();

  return (
    <EditorBlockCustomControls>
      {crop ? (
        <div>
          <Button
            category="light"
            onClick={onCropCancel}
            size="small"
            style={{ marginRight: 5 }}
            text={messages.commonCancel}
          />
          <Button
            category="success"
            icon="check"
            loading={cropLoading}
            onClick={onCropSave}
            size="small"
            text={messages.commonCreate}
          />
        </div>
      ) : setSize ? (
        <div>
          <div style={{ display: 'inline-block', marginRight: 5 }}>
            <Form initialValues={{ maxWidth }}>
              <Range
                field="maxWidth"
                id={`image-max-width-${blockId}`}
                max={100}
                min={20}
                onChange={onSetSizeChange}
                showValue={false}
                stepValue={10}
                style={{ maxWidth: 80, width: 80 }}
              />
            </Form>
          </div>
          <div style={{ display: 'inline-block', marginRight: 5 }}>
            {`${maxWidth}%`}
          </div>
          <Button
            category="light"
            onClick={onSetSizeCancel}
            size="small"
            style={{ marginRight: 5 }}
            text={messages.commonCancel}
          />
          <Button
            category="success"
            icon="check"
            onClick={onSetSizeSave}
            size="small"
            text={messages.commonSave}
          />
        </div>
      ) : replace ? (
        <div>
          <Button
            category="light"
            onClick={onReplaceCancel}
            size="small"
            text={messages.commonCancel}
          />
        </div>
      ) : (
        <div>
          <Button
            category="light"
            data-tooltip={messages.editorBlockImageSetSize}
            icon="arrows-alt-h"
            onClick={onSetSizeClick}
            size="small"
            style={{ marginRight: 5 }}
          />
          <Button
            category="light"
            data-tooltip={messages.editorBlockImageCrop}
            icon="crop"
            onClick={onCropClick}
            size="small"
            style={{ marginRight: 5 }}
          />
          <Button
            // aria-controls connected to id in image alt text field
            aria-controls={`image-alt-text-${blockId}`}
            aria-expanded={altTextVisible ? true : false}
            aria-label={
              altTextVisible
                ? messages.editorBlockImageAltTextRemove
                : messages.editorBlockImageAltTextAdd
            }
            category={altTextVisible ? 'success' : 'light'}
            data-tooltip={
              altTextVisible
                ? messages.editorBlockImageAltTextRemove
                : messages.editorBlockImageAltTextAdd
            }
            onClick={onAltTextToggle}
            size="small"
            style={{ marginRight: 5 }}
            text="ALT"
          />
          {showReplace ? (
            <Button
              category="light"
              data-tooltip={messages.editorBlockImageReplace}
              icon="sync-alt"
              onClick={onReplaceClick}
              size="small"
            />
          ) : null}
        </div>
      )}
    </EditorBlockCustomControls>
  );
};

export default EditorBlockImageControls;
